import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
} from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { deleteUserData } from '../services/database';
import { toast } from 'react-toastify';
import './DeleteAccountPage.css';

const DeleteAccountPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState<'info' | 'confirm'>('info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!currentUser) {
    return (
      <div className="delete-account-page">
        <div className="delete-account-container">
          <h1 className="delete-account-title">Delete Account</h1>
          <p className="delete-account-text">
            You need to be signed in to delete your account.
          </p>
          <button
            className="delete-account-btn delete-account-btn-secondary"
            onClick={() => navigate('/login')}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        password
      );
      await reauthenticateWithCredential(currentUser, credential);
      await deleteUserData(currentUser.uid);
      await deleteUser(currentUser);
      toast.success('Your account has been permanently deleted.');
      navigate('/');
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Failed to delete account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="delete-account-page">
      <div className="delete-account-container">
        <button
          className="delete-account-back"
          onClick={() => (step === 'confirm' ? setStep('info') : navigate(-1))}
        >
          &larr; Back
        </button>

        <div className="delete-account-icon-wrapper">
          <div className="delete-account-icon">&#9888;</div>
        </div>

        <h1 className="delete-account-title">Delete Account</h1>

        {step === 'info' && (
          <>
            <p className="delete-account-text">
              We're sorry to see you go. Before you proceed, please understand
              what happens when you delete your account:
            </p>

            <div className="delete-account-warnings">
              <div className="delete-account-warning-item">
                <span className="warning-bullet">&bull;</span>
                <span>Your profile information will be permanently removed</span>
              </div>
              <div className="delete-account-warning-item">
                <span className="warning-bullet">&bull;</span>
                <span>Your order history will be deleted</span>
              </div>
              <div className="delete-account-warning-item">
                <span className="warning-bullet">&bull;</span>
                <span>Your saved cart items will be lost</span>
              </div>
              <div className="delete-account-warning-item">
                <span className="warning-bullet">&bull;</span>
                <span>This action cannot be undone</span>
              </div>
            </div>

            <div className="delete-account-actions">
              <button
                className="delete-account-btn delete-account-btn-danger"
                onClick={() => setStep('confirm')}
              >
                I Understand, Continue
              </button>
              <button
                className="delete-account-btn delete-account-btn-secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <p className="delete-account-text">
              To confirm deletion, enter your password and type{' '}
              <strong>DELETE</strong> below.
            </p>

            {error && <div className="delete-account-error">{error}</div>}

            <div className="delete-account-form">
              <label className="delete-account-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="delete-account-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />

              <label className="delete-account-label" htmlFor="confirm">
                Type DELETE to confirm
              </label>
              <input
                id="confirm"
                type="text"
                className="delete-account-input"
                placeholder="DELETE"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="delete-account-actions">
              <button
                className="delete-account-btn delete-account-btn-danger"
                onClick={handleDelete}
                disabled={loading || confirmText !== 'DELETE' || !password}
              >
                {loading ? 'Deleting...' : 'Permanently Delete My Account'}
              </button>
              <button
                className="delete-account-btn delete-account-btn-secondary"
                onClick={() => {
                  setStep('info');
                  setError('');
                  setPassword('');
                  setConfirmText('');
                }}
                disabled={loading}
              >
                Go Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountPage;
